import { Check, X, Repeat } from "lucide-react";
import { FaGoogle, FaFacebookF, FaInstagram, FaLinkedinIn } from "react-icons/fa";

const INCLUDED = [
  "Paid ads strategy",
  "Platform recommendation",
  "Campaign setup",
  "Targeting setup",
  "Budget guidance",
  "Remarketing setup where possible",
  "Conversion tracking guidance",
  "Monthly optimisation",
  "Monthly reporting",
  "Lead quality recommendations",
];

const EXCLUDED = [
  "Advertising media budget",
  "Graphic design work",
  "Video production",
  "Product photography",
  "Full website rebuilds",
  "Long-form copywriting",
  "CRM subscription fees",
  "Chatfuel, HubSpot, or third-party tool subscriptions",
  "Sales team management",
];

export function IncludedExcludedSection() {
  return (
    <section
      data-testid="included-excluded-section"
      className="relative py-24 sm:py-32 bg-white"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-3xl mb-12">
          <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-mono mb-4">Clear Scope</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-[-0.03em] leading-[1.05] text-zinc-900">
            What's in and what's not.
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <div className="rounded-3xl border border-zinc-200 bg-white p-7 shadow-sm">
            <div className="flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-[#34a853] font-mono mb-5 font-bold">
              <span className="h-2.5 w-2.5 rounded-full bg-[#34a853]" /> Included
            </div>
            <ul className="space-y-3">
              {INCLUDED.map((item) => (
                <li key={item} className="flex items-start gap-3 text-[15px] text-zinc-800">
                  <span className="h-5 w-5 rounded-full bg-[#34a853]/15 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="h-3 w-3 text-[#34a853]" strokeWidth={3} />
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl border border-zinc-200 bg-zinc-50 p-7">
            <div className="flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-mono mb-5 font-bold">
              <span className="h-2.5 w-2.5 rounded-full bg-zinc-400" /> Excluded
            </div>
            <ul className="space-y-3">
              {EXCLUDED.map((item) => (
                <li key={item} className="flex items-start gap-3 text-[15px] text-zinc-600">
                  <span className="h-5 w-5 rounded-full bg-zinc-200 flex items-center justify-center shrink-0 mt-0.5">
                    <X className="h-3 w-3 text-zinc-500" strokeWidth={3} />
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-8 rounded-2xl border border-[#fbbc04]/40 bg-[#fbbc04]/[0.08] p-5 text-sm text-zinc-800">
          <span className="font-bold text-zinc-900">Important: </span>
          The client pays the advertising budget directly to Google, Meta, LinkedIn, or any other media platform.
        </div>
      </div>
    </section>
  );
}

const CHANNELS = [
  { icon: <FaGoogle />, color: "#4285f4", name: "Google Ads", best: "People already searching for your product or service" },
  { icon: <FaFacebookF />, color: "#1877f2", name: "Facebook Ads", best: "Offers, consumer campaigns, local businesses, awareness and retargeting" },
  { icon: <FaInstagram />, color: "#e1306c", name: "Instagram Ads", best: "Visual products, events, lifestyle brands and creators" },
  { icon: <FaLinkedinIn />, color: "#0a66c2", name: "LinkedIn Ads", best: "B2B leads, professional services, high-ticket offers and decision-makers" },
  { icon: <Repeat className="h-4 w-4" />, color: "#ea4335", name: "Remarketing", best: "Bringing back people who visited, clicked or engaged but didn't enquire" },
];

export function ChannelFitSection() {
  return (
    <section
      data-testid="channel-fit-section"
      className="relative py-24 sm:py-32 bg-zinc-50/60 border-y border-zinc-200"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-3xl mb-10">
          <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-mono mb-4">Channel Fit</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-[-0.03em] leading-[1.05] text-zinc-900">
            Which channel fits you?
          </h2>
        </div>

        <div className="rounded-3xl border border-zinc-200 bg-white overflow-hidden shadow-sm">
          <div className="grid grid-cols-[1.2fr_2fr] sm:grid-cols-[1fr_2fr] bg-zinc-50 border-b border-zinc-200 px-5 sm:px-7 py-4 text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-mono font-bold">
            <div>Channel</div>
            <div>Best for</div>
          </div>
          {CHANNELS.map((c) => (
            <div
              key={c.name}
              data-testid={`channel-row-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="grid grid-cols-[1.2fr_2fr] sm:grid-cols-[1fr_2fr] items-center px-5 sm:px-7 py-5 border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div
                  className="h-10 w-10 rounded-xl flex items-center justify-center"
                  style={{ background: `${c.color}15`, color: c.color }}
                >
                  {c.icon}
                </div>
                <span className="font-display text-base font-bold text-zinc-900 tracking-tight">{c.name}</span>
              </div>
              <div className="text-sm text-zinc-700 leading-relaxed">{c.best}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const PROOF = [
  { text: "SME eCommerce and growth marketing experience", color: "#4285f4" },
  { text: "Paid ads across Google, Meta and LinkedIn", color: "#ea4335" },
  { text: "Practical work with landing pages, automation, CRM, WhatsApp and tracking", color: "#fbbc04" },
  { text: "Focus on leads, sales opportunities and cost control", color: "#34a853" },
  { text: "Clear reporting on leads, cost per lead and campaign performance", color: "#1a73e8" },
];

export function WhyRekisaSection() {
  return (
    <section
      data-testid="why-rekisa-section"
      className="relative py-24 sm:py-32 bg-white"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-[1fr_1.1fr] gap-12 lg:gap-20 items-start">
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-mono mb-4">Why Rekisa</div>
          <h2 className="font-display text-4xl sm:text-6xl lg:text-7xl font-black tracking-[-0.03em] leading-[1.02] text-zinc-900">
            We connect the <span className="g-gradient-text">whole journey</span>.<br />
            Not just the ad.
          </h2>
          <p className="mt-6 text-zinc-600 leading-relaxed text-lg max-w-xl">
            Targeting, landing page, lead capture, automated follow-up, remarketing, tracking, and monthly optimisation — all connected.
          </p>
        </div>
        <ul className="space-y-3">
          {PROOF.map((p, i) => (
            <li
              key={p.text}
              data-testid={`why-rekisa-${i}`}
              className="flex items-start gap-4 rounded-2xl border border-zinc-200 bg-white px-5 py-5 hover:border-zinc-300 hover:shadow-sm transition-all"
            >
              <span
                className="h-9 w-9 rounded-full flex items-center justify-center font-mono font-bold text-sm shrink-0"
                style={{ background: `${p.color}15`, color: p.color }}
              >
                0{i + 1}
              </span>
              <span className="text-[15px] text-zinc-800 leading-snug mt-1.5">{p.text}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
